import React from 'react';
import { FiMail, FiPhone, FiMapPin, FiInfo, FiFacebook, FiInstagram, FiLinkedin } from 'react-icons/fi';
import ScrollReveal from '../components/ScrollReveal';
import { useLanguage } from '../contexts/LanguageContext';

type ContactItemProps = {
    icon: React.ReactNode;
    title: string;
    description: React.ReactNode;
};

const ContactItem: React.FC<ContactItemProps> = ({ icon, title, description }) => {
    return (
        <div className="flex flex-col items-center md:items-start text-center md:text-left mb-6 w-full max-w-sm break-words mx-auto">
            {/* Icon */}
            <div className="bg-[#29519f] p-4 rounded-full inline-flex items-center justify-center mb-4">
                {icon}
            </div>

            {/* Title */}
            <h2 className="text-2xl font-bold text-white mb-2">{title}</h2>

            {/* Description */}
            <p className="text-[#8896ab] break-words whitespace-pre-line">{description}</p>
        </div>
    );
};



const ContactSection: React.FC = () => {
    const { tString } = useLanguage();

    return (
        <section
            className="w-screen "
            style={{
                backgroundColor: '#2a3342',
                backgroundImage: `
                    radial-gradient(ellipse 80% 60% at center, transparent 0%, #2a3342 90%),
                    url('/images/light_pattern.png')
                    `,
                backgroundRepeat: 'repeat',
                backgroundSize: 'auto',
            }}
        >
            <div id="contact" className="scroll-mt-40 w-screen px-16 mt-16 flex flex-col md:flex-row text-left">
                {/* Contact Info / Title */}
                <div className="flex-1 text-white">
                    <ScrollReveal direction="up" delay={0.2} duration={0.8}>
                        <h1 className="text-4xl font-bold text-white mb-10">
                            {tString('contact.title')}<span className="text-[#29519f]">.</span>
                        </h1>
                    </ScrollReveal>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-2 md:gap-10">
                        <ScrollReveal direction="left" delay={0.4} duration={0.8}>
                            <ContactItem
                                icon={
                                    <div className="bg-[#29519f] rounded-full flex items-center justify-center">
                                        <FiMail className="text-white text-xl" />
                                    </div>
                                }
                                title={tString('contact.email')}
                                description="ventas@montelo-express.com"
                            />
                        </ScrollReveal>

                        <ScrollReveal direction="right" delay={0.5} duration={0.8}>
                            <ContactItem
                                icon={
                                    <div className="bg-[#29519f]  rounded-full flex items-center justify-center">
                                        <FiPhone className="text-white text-xl" />
                                    </div>
                                }
                                title={tString('contact.phone')}
                                description={
                                    <>
                                        <span className="font-bold">{tString('contact.phoneLabels.mex')}:</span>
                                        {`\n\t${tString('contact.phoneLabels.office')}:\t(81) 3674 3635 / 36\n\t${tString('contact.phoneLabels.yard')}:\t\t(81) 8385 5768 y (81) 4162 2134\n`}
                                        <span className="font-bold">{tString('contact.phoneLabels.usa')}:</span>
                                        {"\n\t\t\t(210) 409 3563"}
                                    </>
                                }
                            />
                        </ScrollReveal>

                        <ScrollReveal direction="left" delay={0.6} duration={0.8}>
                            <ContactItem
                                icon={
                                    <div className="bg-[#29519f]  rounded-full flex items-center justify-center">
                                        <FiMapPin className="text-white text-xl" />
                                    </div>
                                }
                                title={tString('contact.address')}
                                description={`${tString('contact.addresses.mexico')}`}
                            />
                        </ScrollReveal>

                        <ScrollReveal direction="right" delay={0.7} duration={0.8}>
                            <ContactItem
                                icon={
                                    <div className="bg-[#29519f] rounded-full flex items-center justify-center">
                                        <FiInfo className="text-white text-xl" />
                                    </div>
                                }
                                title={tString('contact.socialMedia')}
                                description={
                                    <div className="flex gap-6 justify-center md:justify-start">
                                        <a
                                            href="https://www.facebook.com/profile.php?id=100060444864509&locale=es_LA"
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className="text-[#8896ab] hover:text-[#1877F2] transition-colors duration-200"
                                        >
                                            <FiFacebook className="text-2xl" />
                                        </a>
                                        <a
                                            href="https://www.instagram.com/montelo_express/"
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className="text-[#8896ab] hover:text-[#E4405F] transition-colors duration-200"
                                        >
                                            <FiInstagram className="text-2xl" />
                                        </a>
                                        <a
                                            href="https://www.linkedin.com/company/montelo-express-s.a.-de-c.v./"
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className="text-[#8896ab] hover:text-[#25D366] transition-colors duration-200"
                                        >
                                            <FiLinkedin className="text-2xl" />
                                        </a>
                                    </div>
                                }
                            />
                        </ScrollReveal>



                    </div>
                </div>

                {/* Contact Form */}
                <div className="flex-1 pt-6">
                    <ScrollReveal direction="up" delay={0.8} duration={0.8}>
                        <form
                            action="https://formsubmit.co/ventas@montelo-express.com"
                            method="POST"
                            className="bg-[#475569] p-10 rounded-lg">
                            {/* Optional hidden inputs */}
                            <input type="hidden" name="_captcha" value="true" />
                            <input type="hidden" name="_subject" value={tString('contact.form.subject')} />
                            <input type="hidden" name="_next" value="https://www.montelo-express.com/" />
                            <div className="mb-6">
                                <label
                                    htmlFor="email"
                                    className="block text-sm font-medium text-[#cbd5e1] mb-2"
                                >
                                    {tString('contact.form.email')}
                                </label>
                                <input
                                    type="email"
                                    id="email"
                                    name="email"
                                    className="w-full p-3 text-sm text-[#e2e8f0] bg-[#475569] rounded-md focus:outline-none focus:ring-2 focus:ring-[#94A3B8] border border-[#94A3B8]"
                                    placeholder={tString('contact.form.emailPlaceholder')}
                                    required
                                />
                            </div>

                            <div className="mb-6">
                                <label
                                    htmlFor="message"
                                    className="block text-sm font-medium text-[#cbd5e1] mb-2"
                                >
                                    {tString('contact.form.message')}
                                </label>
                                <textarea
                                    id="message"
                                    name="message"
                                    className="w-full text-sm p-3 text-[#e2e8f0] bg-[#475569] rounded-md focus:outline-none focus:ring-2 focus:ring-[#94A3B8] border border-[#94A3B8]"
                                    placeholder={tString('contact.form.messagePlaceholder')}
                                    rows={14}
                                    required
                                />
                            </div>

                            <button
                                type="submit"
                                className="w-full py-3 bg-[#29519f] font-bold text-white rounded-md hover:bg-[#1d4bb8] focus:outline-none"
                            >
                                {tString('contact.form.submit')}
                            </button>
                        </form>
                    </ScrollReveal>
                </div>
            </div>
        </section>
    );
};

export default ContactSection;
